<template>
  <section
    v-if="isVisible"
    class="attribute"
    :class="attribute.size"
    :data-attribute-type="attribute.value.type"
  >
    <div class="wrapper">
      <div v-if="canEdit" class="handle">
        <svg-icon :width="'13'" :height="'13'" icon="app-icons/drag" />
      </div>
      <div class="row" :class="{ overlay: contextmenuActive }">
        <svg-icon
          v-tooltip="iconTooltipOptions"
          width="16"
          height="16"
          :icon="attrTypeIcon"
          fill="#586163"
          style="margin-right: 11px"
        />
        <!-- ref attributeName used from AttributeList.vue -->
        <input
          ref="attributeName"
          v-model="name"
          class="name"
          placeholder="Enter a name..."
          :spellcheck="false"
          :disabled="!canEdit"
          tabindex="0"
          @keyup.esc="onBlur"
          @keyup.enter="onBlur"
          @blur="onBlurName($event.target.value)"
        />
        <div
          v-if="canEdit"
          v-tooltip="{ ...tooltipOptions, content: 'Add attribute' }"
          class="add"
          @click="onClickAdd"
        >
          <svg-icon :icon="'app-icons/create'" :width="'16'" :height="'16'" />
        </div>
        <div
          v-if="canEdit"
          v-tooltip="{ ...tooltipOptions, content: 'Resize' }"
          class="size"
          @click="toggle"
        >
          <svg-icon
            :width="'16'"
            :height="'16'"
            :icon="
              attribute.size === 'full'
                ? 'app-icons/collapse'
                : 'app-icons/expand'
            "
          />
        </div>

        <div
          v-if="canEdit"
          v-tooltip="{ ...tooltipOptions, content: 'Delete' }"
          class="remove"
          @click="onClickRemove"
        >
          <svg-icon :width="'16'" :height="'16'" icon="app-icons/trash" />
        </div>
      </div>

      <asset-collection
        v-if="attribute.value.type === 'asset-list'"
        :id="id"
        :class="classObject"
        :assets="attribute.value.data"
        :type="assetType"
        :list-type="listType"
        :allowed-types="assetAllowedTypes"
        :move-action="moveAssetAction"
        :delete-action="deleteAssetAction"
      />

      <component-collection
        v-if="attribute.value.type === 'component-list'"
        :id="id"
        :class="classObject"
        :components="attribute.value.data"
        :type="componentType"
        :list-type="listType"
        :allowed-types="componentAllowedTypes"
        :move-action="moveComponentAction"
        :delete-action="deleteComponentAction"
      />

      <rich-text-editor
        v-if="attribute.value.type === 'string' && !attribute.value.plain"
        ref="attributeEditor"
        :editable="canEdit"
        class="textarea"
        :activate-on-double-click="false"
        :extension-options="attributeExtensionOptions"
        :class="{ disabled: !canEdit }"
        spellcheck="false"
        :content="attribute.value.data"
        @update="onUpdateAttributeContent(attribute.value.data)"
        @blur="onBlurContent"
        @keyup.esc="onBlur"
      />

      <input
        v-if="attribute.value.type === 'string' && attribute.value.plain"
        ref="inputEditor"
        placeholder="Enter string here..."
        :value="attribute.value.data"
        :disabled="!canEdit"
        class="string"
        :class="{
          disabled: !canEdit,
          empty:
            !attribute.value.data ||
            attribute.value.data === '' ||
            attribute.value.data === '<p><br></p>',
        }"
        @keyup.esc="onBlur"
        @keyup.enter="onBlur"
        @blur="onBlurPlainContent($event.target.value)"
      />
    </div>
  </section>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { cloneDeep } from 'lodash/fp';
import { useProtect } from '@/composables/useProtect.js';
import {
  SYS_PERMISSIONS,
  EVENT,
  DRAG_TYPE,
  DRAG_LIST,
  FEATURE_FLAGS,
} from '@/constants.js';
import ComponentCollection from '../../board/ComponentCollection/ComponentCollection.vue';
import AssetCollection from '../../board/AssetCollection/AssetCollection.vue';
import RichTextEditor from '../RichText/RichTextEditor.vue';
import SvgIcon from '../SvgIcon.vue';

export default {
  components: {
    'asset-collection': AssetCollection,
    'component-collection': ComponentCollection,
    'rich-text-editor': RichTextEditor,
    SvgIcon,
  },

  props: {
    id: {
      type: String,
      default: null,
    },
    containerType: {
      type: String,
      default: null,
    },
    containerId: {
      type: String,
      default: null,
    },
  },

  setup() {
    const { hasPermission: canEdit } = useProtect({
      permissions: [SYS_PERMISSIONS.SYS_PROJECTS_EDIT],
      roles: ['editor'],
    });

    return {
      canEdit,
    };
  },

  data() {
    return {
      contextmenuActive: false,
      isVisible: true,
      dragEnter: false,
      componentType: DRAG_TYPE.ATTRIBUTE_COMPONENT,
      assetType: DRAG_TYPE.ATTRIBUTE_ASSET,
      listType: DRAG_LIST.ATTRIBUTES,
      componentAllowedTypes: [
        DRAG_TYPE.COMPONENT,
        DRAG_TYPE.ATTRIBUTE_COMPONENT,
        DRAG_TYPE.SEARCH_COMPONENT,
      ],
      assetAllowedTypes: [
        DRAG_TYPE.ASSET,
        DRAG_TYPE.ATTRIBUTE_ASSET,
        DRAG_TYPE.SEARCH_ASSET,
      ],
      attributeExtensionOptions: {
        bold: true,
        italic: true,
        blockquote: true,
        underline: true,
        customLink: true,
        toolbarOnSelect: true,
        hardbreak: true,
        horizontalRule: false,
        history: true,
        placeholder: {
          text: 'Enter text here...',
        },
        dropEventHandler: true,
        customMention: true,
      },
      name: '',
      tooltipOptions: {
        placement: 'top',
        delay: { show: 800, hide: 0 },
      },
    };
  },

  computed: {
    ...mapGetters('application/drag', ['getItems', 'getSource', 'getTarget']),
    ...mapGetters('project/data/attributes', ['getNode', 'getIndex']),
    ...mapGetters(['getWriteAuthorization']),
    ...mapGetters('project/data', ['getNodeOfType']),
    ...mapGetters('project/data/components', {
      getComponentNodes: 'getNodes',
    }),
    ...mapGetters('project/data/elements', {
      getElementNode: 'getNode',
      getElementNodes: 'getNodes',
    }),
    ...mapGetters('featureFlags', ['getFeatureFlagBySlug']),

    /**
     * Check if the feature is enabled for this workspace
     * @returns {Boolean}
     */
    isAssetListFeatureEnabled() {
      return this.getFeatureFlagBySlug(FEATURE_FLAGS.ASSET_LISTS);
    },

    /**
     * Returns the attribute's container node
     * @returns {Object}
     */
    node() {
      return this.getNodeOfType(this.containerId, this.containerType);
    },

    /**
     * Returns the current attribute
     * @returns {Object}
     */
    attribute() {
      return this.getNode(this.id);
    },

    /**
     * In case the attribute holds a component list returns the components
     * @returns {Array}
     */
    components() {
      return this.getComponentNodes(this.attribute.value.data);
    },

    /**
     * Returns the index of this attribute in the parent component
     * @returns {number}
     */
    index() {
      return this.getIndex(this.id, this.containerType, 'attributes');
    },

    /**
     * Returns whether there is a component currently dragged over the attribute
     * @returns {Boolean}
     */
    isComponentDraggedOver() {
      const { type } = this.getItems;
      return (
        this.dragEnter &&
        (type === DRAG_TYPE.COMPONENT ||
          type === DRAG_TYPE.ATTRIBUTE_COMPONENT ||
          type === DRAG_TYPE.SEARCH_COMPONENT)
      );
    },

    /**
     * Returns whether there is an asset currently dragged over the attribute
     * @returns {Boolean}
     */
    isAssetDraggedOver() {
      const { type } = this.getItems;
      return (
        this.dragEnter &&
        (type === DRAG_TYPE.ASSET ||
          type === DRAG_TYPE.ATTRIBUTE_ASSET ||
          type === DRAG_TYPE.SEARCH_ASSET)
      );
    },

    /**
     * Returns the dynamic classes to be applied
     * @returns {Object}
     */
    classObject() {
      return {
        'component-drag-over': this.isComponentDraggedOver,
        'asset-drag-over': this.isAssetDraggedOver,
        'is-empty': this.components.length === 0,
      };
    },

    /**
     * Returns the corresponding icon based on the attribute type
     * @returns {string}
     */
    attrTypeIcon() {
      switch (this.attribute.value.type) {
        case 'string':
          if (this.attribute.value.plain) {
            return 'app-icons/attribute-string';
          }
          return 'app-icons/attribute-rich-text';
        case 'component-list':
          return 'app-icons/attribute-component-list';
        case 'asset-list':
          return 'app-icons/attribute-asset-list';
        default:
          return '';
      }
    },

    /**
     * Returns the correct content for the type icon tooltip based on the attribute type
     * @returns {object}
     */
    iconTooltipOptions() {
      let name = '';
      switch (this.attribute.value.type) {
        case 'string':
          if (this.attribute.value.plain) {
            name = 'String';
            break;
          }
          name = 'Rich Text';
          break;
        case 'component-list':
          name = 'Component List';
          break;
        case 'asset-list':
          name = 'Asset List';
          break;
        default:
          name = '';
      }
      return {
        content: name,
        placement: 'top',
        delay: { show: 400, hide: 0 },
      };
    },
  },

  /**
   * Mounted vue lifecycle hook
   */
  mounted() {
    this.name = this.attribute.name ?? '';
    // Adjust dragEnter
    $(this.$el).on('dragenter', this.onDragEnter);
    $(this.$el).on('dragleave', this.onDragLeave);
  },

  methods: {
    ...mapActions('application/drag', [
      'setTarget',
      'setItems',
      'setSource',
      'clear',
    ]),
    ...mapActions('project/data/attributes', {
      createAttribute: 'customCreate',
      deleteAttribute: 'customDelete',
      updateAttribute: 'customUpdate',
    }),
    ...mapActions('project/data/attributes', [
      'moveAsset',
      'deleteAsset',
      'moveComponent',
      'deleteComponent',
    ]),
    ...mapActions('application/contextMenu', [
      'createItem',
      'createTitle',
      'setCalcPosition',
      'deleteMenu',
      'setOnDeleteMenu',
    ]),

    /**
     * Event handler for clicking on the remove button
     */
    onClickRemove(event) {
      this.deleteAttribute({ id: this.id, apply: true });
    },

    /**
     * Event handler for blur event for the name field
     */
    onBlurName(value) {
      // Do nothing if current and new value are empty
      if (!value && !this.attribute.name) return;
      // Do nothing if current and new value are the same
      if (this.attribute.name === value) return;
      // Update the name value with the new one
      this.updateAttribute({
        id: this.id,
        props: { name: value },
        apply: true,
      });
    },

    /**
     * Event handler for blur event for the content field
     */
    onBlurContent() {
      const text = this.$refs.attributeEditor.getContent();
      console.log('onBlurContent in Attribute, content is:', text);
      if (text !== this.attribute.value.data) {
        this.updateAttribute({
          id: this.id,
          props: { value: { data: text, type: 'string' } },
          apply: true,
        });
      }
    },

    /**
     * Event handler for blur event for plain text content field
     */
    onBlurPlainContent(value) {
      if (!value && !this.attribute.value.data) return;
      if (this.attribute.value.data === value) return;
      this.updateAttribute({
        id: this.id,
        props: { value: { data: value, type: 'string', plain: true } },
        apply: true,
      });
    },

    /**
     * Event listener - the content is updated (RichTextEditor event)
     */
    onUpdateAttributeContent(content) {
      console.log('Update attribute content called:', content);
      this.updateAttribute({
        id: this.id,
        props: { value: { data: content, type: 'string' } },
        apply: true,
      });
    },

    /**
     * Event handler for click event for the toggle size icon
     */
    toggle() {
      // Find the new attribute size
      const size = this.attribute.size === 'full' ? 'half' : 'full';
      // Update the size value with the new one
      this.updateAttribute({
        id: this.id,
        props: { size },
        apply: true,
      });
    },

    /**
     * Event handler - user presses esc while in name or in contenteditable
     */
    onBlur(event) {
      event.target.blur();
    },

    /**
     * Event handler for clicking on the remove button
     */
    onClickAdd(event) {
      this.createContextMenu(event.clientX, event.clientY);
      this.contextmenuActive = true;
    },

    /**
     * Create a context menu for this component
     * @param {Number} x  The x client coordinate
     * @param {Number} y  The y client coordinate
     */
    async createContextMenu(x, y) {
      // this.createTitle({
      //   title: 'Create new',
      // });
      this.createItem({
        name: 'String field',
        icon: 'app-icons/attribute-string',
        commands: {
          doAction: () => {
            // Create this attribute just before the one with the menu
            this.createAttribute({
              containerId: this.containerId,
              containerType: this.containerType,
              props: { value: { data: '', type: 'string', plain: true } },
              tIndex: this.index + 1,
              apply: true,
            });
            this.deleteMenu();
          },
          isActive: () => {
            return true;
          },
          isEnabled: () => {
            return true;
          },
        },
      });
      this.createItem({
        name: 'Rich Text field',
        icon: 'app-icons/attribute-rich-text',
        commands: {
          doAction: () => {
            // Create this attribute just before the one with the menu
            this.createAttribute({
              containerId: this.containerId,
              containerType: this.containerType,
              props: {},
              tIndex: this.index + 1,
              apply: true,
            });
            this.deleteMenu();
          },
          isActive: () => {
            return true;
          },
          isEnabled: () => {
            return true;
          },
        },
      });
      this.createItem({
        name: 'Component list',
        icon: 'app-icons/attribute-component-list',
        commands: {
          doAction: () => {
            this.createAttribute({
              containerId: this.containerId,
              containerType: this.containerType,
              props: {
                value: { data: [], type: 'component-list' },
              },
              tIndex: this.index + 1,
              apply: true,
            });
            this.deleteMenu();
          },
          isActive: () => {
            return true;
          },
          isEnabled: () => {
            return true;
          },
        },
      });
      if (this.isAssetListFeatureEnabled) {
        this.createItem({
          name: 'Asset list',
          icon: 'app-icons/attribute-asset-list',
          commands: {
            doAction: () => {
              this.createAttribute({
                containerId: this.containerId,
                containerType: this.containerType,
                props: {
                  value: { data: [], type: 'asset-list' },
                },
                tIndex: this.index + 1,
                apply: true,
              });
              this.deleteMenu();
            },
            isActive: () => {
              return true;
            },
            isEnabled: () => {
              return true;
            },
          },
        });
      }
      this.setCalcPosition({
        calcPosition: () => {
          return { left: x, top: y };
        },
      });
      this.setOnDeleteMenu({
        onDeleteMenu: () => {
          this.contextmenuActive = false;
        },
      });
    },

    /**
     * Used as callback to the element's Component Collection.
     */
    moveComponentAction() {
      const source = this.getSource;
      const target = this.getTarget;
      // We use single drag
      const sId = source.ids[0];
      const sIndex = source.indexes[0];
      // In case the target list is the same attribute list or another attribute list
      if (target.type === DRAG_LIST.ATTRIBUTES) {
        // Move this
        this.moveComponent({
          sId,
          sIndex,
          tId: target.id,
          tIndex: target.index,
          apply: true,
        });
      } else if (this.componentType === DRAG_TYPE.ATTRIBUTE_COMPONENT) {
        // Remove the component from the source attribute list
        this.deleteComponent({ id: sId, index: sIndex, apply: true });
      }
      this.clear();
    },

    /**
     * Used as callback to the attribute's Component Collection.
     */
    deleteComponentAction(id, index) {
      // Remove the component from the source attribute list
      this.deleteComponent({ id, index, apply: true });
    },

    /**
     * Used as callback to the element's Asset Collection.
     */
    moveAssetAction() {
      const source = this.getSource;
      const target = this.getTarget;
      // We use single drag
      const sId = source.ids[0];
      const sIndex = source.indexes[0];
      // In case the target list is the same attribute list or another attribute list
      if (target.type === DRAG_LIST.ATTRIBUTES) {
        // Move this
        this.moveAsset({
          sId,
          sIndex,
          tId: target.id,
          tIndex: target.index,
          apply: true,
        });
      } else if (this.assetType === DRAG_TYPE.ATTRIBUTE_ASSET) {
        // Remove the asset from the source attribute list
        this.deleteAsset({ id: sId, index: sIndex, apply: true });
      }
      this.clear();
    },

    /**
     * Used as callback to the attribute's Asset Collection.
     */
    deleteAssetAction(id, index) {
      // Remove the asset from the source attribute list
      this.deleteAsset({ id, index, apply: true });
    },

    /**
     * Focuses the name input of the attribute
     */
    focusName() {
      this.$refs.attributeName.focus();
    },

    /**
     * Focuse the value of the attribute. Return true if focusable and focused, false if not.
     * @returns {boolean} If successful focus
     */
    focusValue() {
      if (this.attribute.value.type === 'string') {
        if (this.attribute.value.plain) {
          this.$refs.inputEditor.focus();
        } else {
          this.$refs.attributeEditor.startEditing();
        }
        return true;
      }
      return false;
    },
  },
};
</script>

<style scoped lang="scss">
::placeholder {
  color: #bcbcbc60;
}

.modal section.half {
  flex: 1 auto;
  flex-grow: 1;
  max-width: 50%;
}

.modal section {
  padding: 5px 20px 5px;
  // margin: 10px 10px 0 10px;
  display: flex;
  flex-direction: column;
  flex: 1 100%;
  max-width: 100%;
  width: 100%;
  transition: max-width 0.5s ease;

  &.sortable-chosen {
    background: #2f383a;
  }

  .wrapper {
    position: relative;
  }

  .handle {
    cursor: move;
    cursor: -webkit-grab;
    opacity: 0.4;
    display: none;
    margin: 0 16px 2px 0;
    position: absolute;
    left: -14px;
    top: 4px;
    :deep(svg circle) {
      fill: #bcbcbc;
    }
  }

  .row {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    margin: 0 0 10px;
    align-items: center;
    height: 20px;
    &.overlay .add,
    &.overlay .remove,
    &.overlay .size {
      display: block !important;
    }

    .name {
      background: transparent;
      padding: 0;
      flex-grow: 1;
      color: #bcbcbc;
      border: none;
      height: 22px;
      width: calc(100% - 107px);
      font-family: 'Open sans';

      &:hover,
      &:focus {
        color: #ddd;
      }

      &:disabled {
        color: #bcbcbc !important;
      }
    }

    .size {
      cursor: pointer;
      opacity: 0.4;
      display: none;
      margin: 0 10px 2px 0;
    }

    .remove {
      cursor: pointer;
      opacity: 0.4;
      display: none;
      margin: 0 0 2px 0;
    }

    .add {
      cursor: pointer;
      opacity: 0.4;
      display: none;
      margin: 0 10px 2px 0;
      width: 16px;
    }
  }

  input.string {
    background: #252929;
    padding: 10px 12px 11px;
    border: none;
    border-radius: 3px;
    width: 100%;
    font-size: 14px;
    color: #bcbcbc;
    font-family: 'open sans';
    min-height: 42px;

    &:empty:before {
      cursor: default;
      visibility: hidden;
    }

    &.disabled {
      background: none;
      border: 1px solid #3c4f52;
      color: #bcbcbc !important;
      cursor: default;
      &.empty {
        opacity: 0.6;
      }
    }
  }
}

.modal section:hover .remove,
.modal section:hover .handle,
.modal section:hover .size,
.modal section:hover .add {
  display: block;
}

.modal section .remove:hover,
.modal section .handle:hover,
.modal section .size:hover,
.modal section .add:hover {
  opacity: 0.8;
}

.modal section .textarea {
  padding: 10px;
  /* min-height:40px;*/
  background: #252929;
  border-radius: 3px;
  clear: both;
  color: #bcbcbc;
  word-break: break-word;
  overflow: hidden;
  // transition: background 0.2s;
  &:empty:before {
    cursor: default;
    visibility: hidden;
  }
  &.disabled {
    background: none;
    border: 1px solid #3c4f52;
    color: #bcbcbc !important;
    cursor: default;
  }

  &:hover,
  &:focus {
    color: #ddd;
  }

  &:focus {
    background: #1f2121;
  }

  &:empty:before {
    content: attr(placeholder);
    opacity: 0.6;
    display: block; /* For Firefox */
    cursor: text;
  }
}
</style>
